import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell, { PageContainer, PageHeader } from "@/components/AppShell";
import { useAuth } from "@/lib/AuthContext";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import InstantGamingBanner from "@/components/InstantGamingBanner";
import {
  Users,
  Sparkles,
  Zap,
  Globe2,
  Swords,
  Bot,
  TrendingUp,
  ArrowUpRight,
  Skull,
  Search,
  Gamepad2,
} from "lucide-react";

export default function DashboardPage() {
  const { t, lang } = useT();
  const { user, profile } = useAuth();
  const name = profile?.nickname || "";
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api
      .get("/stats/public")
      .then((r) => setStats(r.data))
      .catch(() => setStats({ users_total: 0, users_online: 0, premium_subscribers: 0, countries_active: 0, games_tracked: 0 }));
  }, []);

  const cards = [
    { to: "/app/players", icon: Search, title: t("dash.players_card") || "Mitspieler finden", desc: t("dash.players_desc") || "Match-Score basierend auf Spiel, Land, Sprache und Skill — keine Bots, echte Pilots.", accent: "from-neon/30 to-transparent", tid: "card-players" },
    { to: "/app/zombies", icon: Skull, title: t("lp.f7.title"), desc: t("lp.f7.desc"), accent: "from-lime-500/25 to-transparent", tid: "card-zombies" },
    { to: "/app/teammates", icon: Users, title: t("nav.teammates"), desc: t("lp.f1.desc"), accent: "from-cyan-500/20 to-transparent", tid: "card-teammates" },
    { to: "/app/coach", icon: Bot, title: t("nav.coach"), desc: t("lp.f6.desc"), accent: "from-neon/25 to-transparent", tid: "card-coach" },
    { to: "/app/play", icon: Sparkles, title: t("nav.play"), desc: t("lp.f2.desc"), accent: "from-fuchsia-500/20 to-transparent", tid: "card-play" },
    { to: "/app/trends", icon: Globe2, title: t("nav.trends"), desc: t("lp.f4.desc"), accent: "from-emerald-500/20 to-transparent", tid: "card-trends" },
    { to: "/app/builds", icon: Swords, title: t("nav.builds"), desc: t("lp.f5.desc"), accent: "from-red-500/20 to-transparent", tid: "card-builds" },
    { to: "/app/fps-fix", icon: Zap, title: t("nav.fps"), desc: t("lp.f3.desc"), accent: "from-amber-500/20 to-transparent", tid: "card-fps" },
  ];

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          kicker={profile?.flag ? `${profile.flag} ${profile.countryName || ""}` : t("dash.kicker")}
          title={
            <>
              {t("dash.welcome")} <span className="text-neon">{name}</span>.
            </>
          }
          subtitle={t("dash.sub")}
        />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <MiniStat
            label={t("dash.pilots") || "Pilots"}
            value={stats == null ? "—" : (stats.users_total ?? 0).toLocaleString()}
            delta={t("dash.early_access") || "Early access"}
            tid="stat-pilots"
          />
          <MiniStat
            label={t("dash.online") || "Online now"}
            value={stats == null ? "—" : (stats.users_online ?? 0).toLocaleString()}
            delta={t("dash.live") || "live"}
            tid="stat-online"
          />
          <MiniStat
            label={t("dash.countries") || "Countries"}
            value={stats == null ? "—" : (stats.countries_active ?? 0).toLocaleString()}
            delta={t("dash.active") || "active"}
            tid="stat-countries"
          />
          <MiniStat
            label={t("dash.your_games") || "Your games"}
            value={(profile?.games?.length || 0).toLocaleString()}
            delta={t("dash.saved") || "saved"}
            tid="stat-your-games"
          />
        </div>

        {/* INSTANT GAMING — Partner deals for pilots (logged-in only) */}
        <section
          className="mb-6 sm:mb-8"
          aria-labelledby="dash-ig-heading"
          data-testid="dash-ig-section"
        >
          <div className="flex items-center justify-between mb-3">
            <h2
              id="dash-ig-heading"
              className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono inline-flex items-center gap-2"
            >
              {t("lp.ig.title") || "🔥 Deals für Piloten"}
            </h2>
            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-widest">
              {t("lp.ig.kicker") || "Partner Deals"}
            </span>
          </div>
          <div className="relative group">
            <div
              aria-hidden="true"
              className="absolute -inset-px rounded-2xl bg-gradient-to-r from-neon/30 via-cyan-400/20 to-neon/30 opacity-30 blur-md transition-opacity duration-500 group-hover:opacity-60 pointer-events-none"
            />
            <div className="relative rounded-2xl border border-neon/25 bg-zinc-950/70 backdrop-blur-sm p-3 sm:p-4 overflow-hidden">
              <InstantGamingBanner lang={lang === "de" ? "de" : "en"} />
            </div>
          </div>
          <p className="mt-2 text-[11px] text-zinc-500 text-center">
            {t("lp.ig.affiliate") || "Affiliate-Partner von Instant Gaming"}
          </p>
        </section>

        {/* Favorite games — quick path into matchmaking */}
        {profile?.games?.length > 0 && (
          <section className="mb-6 sm:mb-8" data-testid="dash-favorites">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono inline-flex items-center gap-2">
                <Gamepad2 size={12} className="text-neon" />
                {t("dash.favs_h") || "Deine Lieblingsgames · Mitspieler finden"}
              </h2>
              <Link
                to="/app/players"
                className="text-[11px] text-neon hover:underline"
                data-testid="dash-all-players"
              >
                {t("dash.all_players") || "Alle Pilots"} →
              </Link>
            </div>
            <div className="flex flex-wrap gap-2">
              {profile.games.map((g) => (
                <Link
                  key={g}
                  to={`/app/players?game=${encodeURIComponent(g)}`}
                  data-testid={`dash-fav-game-${g}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/5 border border-white/10 text-zinc-200 hover:bg-neon/10 hover:border-neon/40 hover:text-neon transition-colors"
                >
                  <Gamepad2 size={12} />
                  {g}
                  <ArrowUpRight size={11} className="opacity-60" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Game Hubs — live KI guides per game */}
        <section className="mb-6 sm:mb-8" data-testid="dash-hubs">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono inline-flex items-center gap-2">
              <Sparkles size={12} className="text-neon" />
              {(lang || "").startsWith("de") ? "Game Hubs · Live KI-Guides" : "Game Hubs · Live AI Guides"}
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { slug: "valorant", name: "Valorant" },
              { slug: "cs2", name: "CS2" },
              { slug: "warzone", name: "Warzone" },
              { slug: "cod-zombies", name: "CoD Zombies" },
              { slug: "apex", name: "Apex" },
              { slug: "league-of-legends", name: "LoL" },
              { slug: "fortnite", name: "Fortnite" },
              { slug: "overwatch2", name: "Overwatch 2" },
              { slug: "elden-ring", name: "Elden Ring" },
              { slug: "ea-fc-25", name: "EA FC 25" },
            ].map((h) => (
              <Link
                key={h.slug}
                to={`/app/hub/${h.slug}`}
                data-testid={`dash-hub-${h.slug}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/5 border border-white/10 text-zinc-200 hover:bg-neon/10 hover:border-neon/40 hover:text-neon transition-colors"
              >
                {h.name}
                <ArrowUpRight size={11} className="opacity-60" />
              </Link>
            ))}
          </div>
        </section>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.to}
                to={c.to}
                data-testid={c.tid}
                className="group card-interactive relative overflow-hidden rounded-2xl border border-white/5 bg-zinc-900/50 backdrop-blur-sm p-6 animate-fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${c.accent} opacity-60 pointer-events-none`} />
                <div className="relative">
                  <div className="w-11 h-11 rounded-xl bg-neon/10 border border-neon/25 grid place-items-center text-neon">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-4 font-display text-xl font-bold">{c.title}</h3>
                  <p className="mt-2 text-sm text-zinc-400">{c.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm text-neon font-semibold">
                    {t("dash.open")}
                    <ArrowUpRight
                      size={16}
                      className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </PageContainer>
    </AppShell>
  );
}

function MiniStat({ label, value, delta, tid }) {
  return (
    <div className="rounded-2xl bg-zinc-900/60 border border-white/5 p-4" data-testid={tid}>
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono">{label}</div>
      <div className="mt-1 font-display text-2xl font-black" data-testid={`${tid}-value`}>{value}</div>
      <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-neon">
        <TrendingUp size={12} />
        {delta}
      </div>
    </div>
  );
}
